﻿import { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHask } from '@/hooks/useHask';
import { useCustomerDetail } from '@/hooks/useCustomerDetail';
import { useToast } from '@/components/common/Toast';
import PageHeader from '@/components/common/PageHeader';
import Spinner from '@/components/common/Spinner';
import CompanySelect from '@/components/common/CompanySelect';
import CustomerSelect from '@/components/common/CustomerSelect';
import CustomerDetail from '@/components/visits/CustomerDetail';
import QuestionForm from '@/components/visits/QuestionForm';
import NewProspectModal from '@/components/visits/NewProspectModal';
import { messaggioDaErrore } from '@/services/api';

export default function NewVisitPage() {
  const { t } = useTranslation('visits');
  const { t: tc } = useTranslation();
  const { showToast } = useToast();
  const { questions, loading, loadQuestions, setAnswer, getAnswers, validate, validateDetailed, save, resetAnswers } = useHask();
  const { customer, fetchCustomer, clear: clearCustomer } = useCustomerDetail();

  const [codazienda, setCodazienda] = useState('0');
  const [codcliente, setCodcliente] = useState('0');
  const [showProspectModal, setShowProspectModal] = useState(false);
  const [isNewProspect, setIsNewProspect] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch customer detail when client changes
  useEffect(() => {
    if (codcliente !== '0') {
      fetchCustomer(codcliente);
    } else {
      clearCustomer();
    }
  }, [codcliente, fetchCustomer, clearCustomer]);

  // Load questionnaire after client is selected
  useEffect(() => {
    if (codazienda !== '0' && codcliente !== '0') {
      localStorage.setItem('hask_codarea', 'VISITA');
      localStorage.setItem('hask_codice', codazienda);
      loadQuestions('VISITA', codazienda, '*');
    } else {
      resetAnswers();
    }
  }, [codazienda, codcliente, loadQuestions, resetAnswers]);

  // Handle "Nuovo Prospect" checkbox
  const handleNewProspectToggle = (checked: boolean) => {
    setIsNewProspect(checked);
    if (checked) {
      // Reset current selection and open modal
      setCodcliente('0');
      clearCustomer();
      resetAnswers();
      setShowProspectModal(true);
    }
  };

  // Handle modal close without saving
  const handleProspectModalClose = () => {
    setShowProspectModal(false);
    setIsNewProspect(false);
  };

  // Handle prospect saved successfully
  const handleProspectSaved = (savedCodazienda: string, savedCodcliente: string) => {
    setShowProspectModal(false);
    setIsNewProspect(false);
    // Auto-select the new prospect
    setCodazienda(savedCodazienda);
    setCodcliente(savedCodcliente);
  };

  const handleAnswerChange = (codattr: string, value: string) => {
    setAnswer(codattr, value);
    validate();
  };

  const handleSave = async () => {
    const { valid, missing } = validateDetailed();
    if (!valid) {
      const campo = missing?.domanda?.trim();
      const messaggio = campo
        ? 'Compila il campo obbligatorio: ' + campo
        : 'Compila tutti i campi obbligatori';
      showToast('Attenzione', messaggio, 'warning');
      return;
    }
    setSaving(true);
    try {
      await save(codazienda, codcliente, files);
      showToast(tc('actions.save'), t('saveSuccess'), 'success');
      setCodazienda('0');
      setCodcliente('0');
      setFiles([]);
      resetAnswers();
      clearCustomer();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const dettaglio = messaggioDaErrore(err);
      console.error('Errore nel salvataggio delle risposte:', err);
      showToast('Errore', 'Errore nel salvataggio delle risposte: ' + dettaglio, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const showForm = codazienda !== '0' && codcliente !== '0';

  return (
    <>
      <PageHeader title={t('newVisit')} />
      <div className="container py-3">
        <Card className="mb-3">
          <Card.Header>
            <Row className="align-items-center g-2 pt-3">
              <Col md={3}>
                <CompanySelect value={codazienda}
                  onChange={v => { setCodazienda(v); setCodcliente('0'); resetAnswers(); clearCustomer(); }} />
              </Col>
              <Col md={5}>
                {!isNewProspect && (
                  <CustomerSelect codazienda={codazienda} value={codcliente} onChange={setCodcliente} />
                )}
              </Col>
              <Col md={2} className="d-flex align-items-center pt-2">
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id="new-prospect"
                    checked={isNewProspect}
                    onChange={e => handleNewProspectToggle(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="new-prospect">{t('newProspect')}</label>
                </div>
              </Col>
            </Row>
          </Card.Header>

          {customer && <CustomerDetail customer={customer} />}
        </Card>

        {showForm && loading && <Spinner text={tc('labels.loading')} />}

        {showForm && !loading && questions.length > 0 && (
          <Card>
            <Card.Body>
              <QuestionForm
                questions={questions}
                answers={getAnswers()}
                onAnswer={handleAnswerChange}
              />

              <hr className="my-3" />

              <Form.Group className="mb-3">
                <Form.Label>{t('attachments')}</Form.Label>
                <Form.Control type="file" multiple ref={fileInputRef}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiles(e.target.files ? Array.from(e.target.files) : [])} />
                {files.length > 0 && (
                  <Form.Text>{t('filesSelected', { count: files.length })}</Form.Text>
                )}
              </Form.Group>

              <Button variant="primary" className="w-100" size="lg" onClick={handleSave} disabled={saving}>
                {saving ? '...' : tc('actions.save')}
              </Button>
            </Card.Body>
          </Card>
        )}
      </div>

      <NewProspectModal
        show={showProspectModal}
        preselectedAzienda={codazienda !== '0' ? codazienda : undefined}
        onClose={handleProspectModalClose}
        onSaved={handleProspectSaved}
      />
    </>
  );
}

